import { Box, Button, Heading, IconButton, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { ArrowLeft, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Blocker, NavigateFunction } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ConfirmDialog, LoadingState, Modal } from '@mangovoice/mango-ui'

import type { SwitchboardApi } from '../api/switchboardApi'
import {
  extensionMapAtom,
  externalTransferPhoneNumberDigitLimitsAtom,
  getDraftFromStorage,
  getDraftWithMetaFromStorage,
  messagingGroupsAtom,
  messagingGroupsLoadedAtom,
  schedulesAtom,
  useSwitchboardDraft,
} from '../atoms'
import { FixedHandleRouteConflictToast } from '../components/FixedHandleRouteConflictToast'
import { SwitchboardDeleteDialog } from '../components/list/SwitchboardDeleteDialog'
import { NodePalette } from '../components/NodePalette'
import { Z_INDEX } from '../constants/styling'
import type { LegacyFormatNode } from '../converters/shared/legacyFormatTypes'
import { validateSpecCompliance } from '../converters/shared/specComplianceValidator'
import { extractNumericId } from '../converters/shared/uuidUtils'
import { validateRequiredFieldValues } from '../converters/shared/validators'
import { convertReactFlowToLegacyFormat } from '../converters/toLegacy/index'
import {
  deserializeSwitchboardData,
  getSwitchboardDisplayName,
  NodeType,
  processDayNightSettings,
  SwitchboardCanvas,
  SwitchboardFlowData,
  useGetPbx,
  useGetSwitchboard,
  useUpdateSwitchboard,
} from '../index'
import type { SwitchboardTranslateFn } from '../types'
import { findFixedHandleRouteConflicts } from '../utils/fixedHandleRouteConflicts'
import { getPersistedGraphSignature } from '../utils/switchboardFlowSignature'
import { text } from '../utils/text'
import { findUnconfiguredConnectedScheduleNodes } from '../utils/unconfiguredScheduleNodes'
import { getFixedHandleRouteConflictDescription } from './fixedHandleRouteConflictPresentation'
import {
  getDraftComparableFlowSignature,
  hasDraftRelevantChanges,
} from './switchboardDraftSignature'
import { resolveInitialFlowOwnership } from './switchboardInitialFlowOwnership'
import { resolvePostSaveFlowData } from './switchboardPostSaveSync'
import {
  clearSavedSwitchboardSnapshotFromStorage,
  getSavedSwitchboardSnapshotFromStorage,
  isSavedSwitchboardSnapshotOlderThanServer,
  isSavedSwitchboardSnapshotUsable,
  type SavedSwitchboardSnapshot,
  saveSwitchboardSnapshotToStorage,
} from './switchboardSavedSnapshot'
import {
  flushPendingInputBeforeSave,
  isSwitchboardSaveTargetCurrent,
} from './switchboardSaveUtils'

const FOCUS_REFRESH_THROTTLE_MS = 750

export interface SwitchboardEditPageProps {
  /** Switchboard API instance */
  api: SwitchboardApi
  /** Extension dial plan ID from route params */
  extensionDialPlan: number
  /** Navigation function (from react-router useNavigate) */
  navigate: NavigateFunction
  /** Route path for the switchboards list page */
  listRoute: string
  /** PBX ID that this switchboard belongs to */
  pbxId?: number
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Optional delete handler function */
  onDelete?: () => void | Promise<void>
  /**
   * Mango internal / staff: when true, internal-only switchboard UI is enabled.
   * Not the same as {@link isSuperuser}; superuser access should check both values.
   */
  isStaff?: boolean
  /**
   * Whether the user is a superuser (Django / product).
   * Accepted for compatibility with callers that pass role context.
   */
  isSuperuser?: boolean
  /**
   * Optional navigation blocker from react-router's useBlocker hook.
   * Pass this to show a confirmation dialog when user tries to navigate away with unsaved changes.
   * The consuming app should call: useBlocker(hasUnsavedChanges) and pass the result here.
   * @example
   * const blocker = useBlocker(hasUnsavedChanges)
   * <SwitchboardEditPage blocker={blocker} onUnsavedChangesChange={setHasUnsavedChanges} ... />
   */
  blocker?: Blocker
  /**
   * Callback fired when the unsaved changes state changes.
   * Use this to sync with useBlocker in the consuming app.
   * @example
   * const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
   * const blocker = useBlocker(hasUnsavedChanges)
   * <SwitchboardEditPage onUnsavedChangesChange={setHasUnsavedChanges} blocker={blocker} ... />
   */
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void
  /** Color mode for React Flow theming ('light' | 'dark'). Defaults to 'light'. */
  colorMode?: 'light' | 'dark'
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  /**
   * Optional refresh callback for reference datasets owned by consuming apps
   * (for example, sound clips in web-app/admin3 wrappers).
   */
  onRefreshReferenceData?: () => Promise<void> | void
  // Note: Reference data (extensionsList, messagingGroups, etc.) is provided via Jotai atoms
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms before rendering this component
}

export const SwitchboardEditPage: React.FC<SwitchboardEditPageProps> = ({
  api,
  extensionDialPlan,
  navigate,
  listRoute,
  pbxId,
  toast,
  onDelete,
  isStaff = false,
  blocker,
  onUnsavedChangesChange,
  colorMode = 'light',
  t,
  onRefreshReferenceData,
}) => {
  const {
    data: switchboard,
    isLoading,
    error,
    refetch: refetchSwitchboard,
  } = useGetSwitchboard({
    api,
    extensionDialPlan,
  })
  const updateMutation = useUpdateSwitchboard({
    api,
    invalidateOnSuccess: false,
  })
  const queryClient = useQueryClient()
  const externalTransferPhoneNumberDigitLimits = useAtomValue(
    externalTransferPhoneNumberDigitLimitsAtom,
  )

  // Fetch PBX data for day/night settings
  const { data: pbx, refetch: refetchPbx } = useGetPbx({
    api,
    pbxId: pbxId ?? 0,
    options: {
      enabled: !!pbxId && pbxId > 0,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  })

  // Process day/night settings from PBX data
  const dayNightSettings = useMemo(() => {
    return processDayNightSettings(pbx ?? null)
  }, [pbx])

  // Note: Jotai atoms should be set in the consuming app (e.g., admin3)
  // using the `useSwitchboardDataAtoms` hook where data is fetched.
  // Props are kept for backward compatibility and as fallback for atoms.

  // Get switchboard display name from extension
  // Note: extensionMap should be set via useSwitchboardDataAtoms in consuming app
  const extensionMap = useAtomValue(extensionMapAtom)
  const messagingGroups = useAtomValue(messagingGroupsAtom)
  const messagingGroupsLoaded = useAtomValue(messagingGroupsLoadedAtom)
  const schedules = useAtomValue(schedulesAtom)
  const signatureRefContext = useMemo(
    () => ({ messagingGroups, messagingGroupsLoaded }),
    [messagingGroups, messagingGroupsLoaded],
  )
  const switchboardDisplayName = useMemo(() => {
    if (!extensionMap || !switchboard) return null
    const extension = extensionMap.get(switchboard.extension_dial_plan)
    return getSwitchboardDisplayName(extension)
  }, [extensionMap, switchboard])

  // Calculate flowData before any early returns to maintain hook order
  const flowData = useMemo(() => {
    if (!switchboard?.json_data) {
      return { nodes: [], edges: [] }
    }
    return deserializeSwitchboardData(switchboard.json_data)
  }, [switchboard?.json_data])

  // Ensure flowData has nodes and edges arrays (before early returns)
  const nodes = flowData?.nodes || []
  const edges = flowData?.edges || []
  const hasOldFormat = Boolean(
    nodes.length === 0 &&
    edges.length === 0 &&
    switchboard?.json_data &&
    typeof switchboard.json_data === 'string' &&
    switchboard.json_data.trim() !== '',
  )

  const { saveDraft, clearDraft } = useSwitchboardDraft(
    extensionDialPlan,
    switchboard?.updated_at,
  )

  // Draft: 1) Sync read from localStorage for initial state (atomWithStorage hydrates async)
  //        2) Save/clear via Jotai hook. Parent must use key={extensionDialPlan} when switching.
  const { initialFlowData, hadDraftOnMount, savedSnapshotOnMount } = useMemo(
    () =>
      resolveInitialFlowOwnership({
        extensionDialPlan,
        serverFlowData: flowData,
        serverUpdatedAt: switchboard?.updated_at,
        readSavedSnapshot: getSavedSwitchboardSnapshotFromStorage,
        readDraft: (id) => getDraftFromStorage(id, switchboard?.updated_at),
        readDraftMeta: (id) =>
          getDraftWithMetaFromStorage(id, switchboard?.updated_at),
      }),
    [extensionDialPlan, flowData, switchboard?.updated_at],
  )

  const [currentFlowData, setCurrentFlowData] =
    useState<SwitchboardFlowData>(initialFlowData)
  const fixedHandleRouteConflicts = useMemo(
    () => findFixedHandleRouteConflicts(currentFlowData),
    [currentFlowData],
  )
  const routeConflictDescriptions = useMemo(
    () =>
      fixedHandleRouteConflicts.map((conflict) =>
        getFixedHandleRouteConflictDescription({
          conflict,
          flowData: currentFlowData,
          schedules,
          t,
        }),
      ),
    [currentFlowData, fixedHandleRouteConflicts, schedules, t],
  )
  const hasFixedHandleRouteConflicts = fixedHandleRouteConflicts.length > 0
  const [pendingNodeType, setPendingNodeType] = useState<NodeType | null>(null)
  const [isRevertDialogOpen, setIsRevertDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeletingSwitchboard, setIsDeletingSwitchboard] = useState(false)
  const [isVersionConflictDialogOpen, setIsVersionConflictDialogOpen] =
    useState(false)
  const [pendingServerFlowData, setPendingServerFlowData] =
    useState<SwitchboardFlowData | null>(null)
  const [pendingServerUpdatedAt, setPendingServerUpdatedAt] = useState<
    string | undefined
  >(undefined)

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(hadDraftOnMount)
  const [canvasHydrateToken, setCanvasHydrateToken] = useState(0)
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges)
  hasUnsavedChangesRef.current = hasUnsavedChanges
  const editorBaseUpdatedAtRef = useRef<string | undefined>(undefined)
  const isFocusRefreshInFlightRef = useRef(false)
  const isReferenceRefreshInFlightRef = useRef(false)
  const lastFocusRefreshAtRef = useRef(0)
  const hasRunInitialRefreshRef = useRef(false)
  const acknowledgedConflictUpdatedAtRef = useRef<string | undefined>(undefined)
  // Refs: lastStructure (avoid redundant updates), justSaved (suppress post-save echo), isFirstDataChange (skip initial canvas onDataChange)
  const lastStructureRef = useRef<string>('')
  const justSavedRef = useRef(false)
  const isFirstDataChangeRef = useRef(true)
  const hasCheckedDraftFreshnessRef = useRef(false)
  const latestFlowDataRef = useRef<SwitchboardFlowData>(initialFlowData)
  const savedSnapshotRef = useRef<SavedSwitchboardSnapshot | null>(
    savedSnapshotOnMount,
  )
  const restoredDraftInitiallyRef = useRef(hadDraftOnMount)
  const flushPendingDataChangeRef = useRef<() => void>(() => {})

  useEffect(() => {
    hasRunInitialRefreshRef.current = false
    hasCheckedDraftFreshnessRef.current = false
  }, [extensionDialPlan])

  useEffect(() => {
    savedSnapshotRef.current = savedSnapshotOnMount
  }, [extensionDialPlan, savedSnapshotOnMount])

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  // beforeunload: warn on close/refresh when unsaved
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        // Best-effort: commit pending editor changes before unload so refresh
        // preserves local draft field edits.
        flushPendingInputBeforeSave(flushPendingDataChangeRef.current)
        saveDraft(
          latestFlowDataRef.current,
          editorBaseUpdatedAtRef.current ?? switchboard?.updated_at,
        )
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [saveDraft, switchboard?.updated_at])

  const hasInitializedFromApiRef = useRef(hadDraftOnMount)
  // Last server/draft signature applied to the canvas; set by the hydrate effects.
  const lastHydratedFlowSignatureRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (flowData.nodes.length === 0 && flowData.edges.length === 0) return

    const serverFlowSignature = getDraftComparableFlowSignature(
      flowData,
      signatureRefContext,
    )
    const savedSnapshot = savedSnapshotRef.current
    if (savedSnapshot) {
      if (!isSavedSwitchboardSnapshotUsable(savedSnapshot, extensionDialPlan)) {
        savedSnapshotRef.current = null
        clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
      } else if (
        isSavedSwitchboardSnapshotOlderThanServer(
          savedSnapshot,
          switchboard?.updated_at,
        )
      ) {
        savedSnapshotRef.current = null
        clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
      } else {
        const serverPersistedSignature = getPersistedGraphSignature(
          flowData,
          signatureRefContext,
        )
        if (serverPersistedSignature.status === 'pending') return

        if (serverPersistedSignature.value === savedSnapshot.contentSignature) {
          savedSnapshotRef.current = null
          clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
        } else {
          const currentSignature = getPersistedGraphSignature(
            latestFlowDataRef.current,
            signatureRefContext,
          )
          if (
            currentSignature.status !== 'pending' &&
            currentSignature.value !== savedSnapshot.contentSignature
          ) {
            justSavedRef.current = true
            isFirstDataChangeRef.current = true
            hasUnsavedChangesRef.current = false
            setHasUnsavedChanges(false)
            setCurrentFlowData(savedSnapshot.flowData)
            setCanvasHydrateToken((token) => token + 1)
            latestFlowDataRef.current = savedSnapshot.flowData
            editorBaseUpdatedAtRef.current = savedSnapshot.updatedAt
          }
          lastHydratedFlowSignatureRef.current = savedSnapshot.contentSignature
          return
        }
      }
    }

    // Initial hydration: seed the canvas from server data exactly once, unless
    // a local draft was restored on mount (then the draft owns the canvas).
    // The canvas seeds its own React Flow state from `initialFlowData` (the
    // value captured at mount). On a SPA remount the query cache can serve a
    // stale-first read, so `flowData` may already have settled to a newer
    // server version than the canvas was seeded with. When that happens (and
    // there is no restored draft), force a full canvas re-hydration so a
    // data-only change (e.g. a schedule id) is not lost behind React Flow's
    // structure-only sync.
    if (!hasInitializedFromApiRef.current) {
      hasInitializedFromApiRef.current = true
      lastHydratedFlowSignatureRef.current = serverFlowSignature
      setCurrentFlowData(flowData)
      latestFlowDataRef.current = flowData
      editorBaseUpdatedAtRef.current = switchboard?.updated_at
      // Same reference ⇒ same signature; skip a second round-trip.
      const seedDiffers =
        initialFlowData !== flowData &&
        serverFlowSignature !==
          getDraftComparableFlowSignature(initialFlowData, signatureRefContext)
      if (!hadDraftOnMount && seedDiffers) {
        justSavedRef.current = true
        isFirstDataChangeRef.current = true
        setCanvasHydrateToken((token) => token + 1)
      }
      return
    }

    // Re-sync guard: after the initial hydration, the query cache can settle to
    // a different server version than the one we first seeded. The canvas only
    // picks up data-only changes via hydrateToken, so when the editor is clean
    // (no unsaved local changes and no restored draft) and the server graph
    // content differs from what we last pushed, force a full re-hydration so
    // the canvas never gets stuck on stale data.
    if (hasUnsavedChangesRef.current) return
    if (hadDraftOnMount) return
    if (serverFlowSignature === lastHydratedFlowSignatureRef.current) return

    lastHydratedFlowSignatureRef.current = serverFlowSignature
    justSavedRef.current = true
    isFirstDataChangeRef.current = true
    setCurrentFlowData(flowData)
    setCanvasHydrateToken((token) => token + 1)
    latestFlowDataRef.current = flowData
    editorBaseUpdatedAtRef.current = switchboard?.updated_at
  }, [
    extensionDialPlan,
    flowData,
    hadDraftOnMount,
    initialFlowData,
    signatureRefContext,
    switchboard?.updated_at,
  ])

  const handleAddNode = useCallback((nodeType: NodeType) => {
    setPendingNodeType(nodeType)
  }, [])

  const handleNodeAdded = useCallback(() => {
    setPendingNodeType(null)
  }, [])

  const handleDelete = useCallback(() => {
    setIsDeleteDialogOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!onDelete) return

    setIsDeletingSwitchboard(true)
    try {
      clearDraft()
      clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
      savedSnapshotRef.current = null
      await onDelete()
      setIsDeleteDialogOpen(false)
    } finally {
      setIsDeletingSwitchboard(false)
    }
  }, [clearDraft, extensionDialPlan, onDelete])

  useEffect(() => {
    if (!restoredDraftInitiallyRef.current) return
    if (hasCheckedDraftFreshnessRef.current) return
    if (!switchboard?.updated_at) return

    hasCheckedDraftFreshnessRef.current = true

    const draftStillValid = getDraftFromStorage(
      extensionDialPlan,
      switchboard.updated_at,
    )
    if (draftStillValid) {
      editorBaseUpdatedAtRef.current = switchboard.updated_at
      return
    }

    // Draft is stale compared to DB - restore server state.
    clearDraft()
    clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
    savedSnapshotRef.current = null
    justSavedRef.current = true
    isFirstDataChangeRef.current = true
    hasUnsavedChangesRef.current = false
    setHasUnsavedChanges(false)
    setCurrentFlowData(flowData)
    setCanvasHydrateToken((token) => token + 1)
    latestFlowDataRef.current = flowData
    lastHydratedFlowSignatureRef.current = getDraftComparableFlowSignature(
      flowData,
      signatureRefContext,
    )
    editorBaseUpdatedAtRef.current = switchboard.updated_at
  }, [
    clearDraft,
    extensionDialPlan,
    flowData,
    signatureRefContext,
    switchboard?.updated_at,
  ])

  const handleRegisterFlushPendingDataChange = useCallback(
    (flushPendingDataChange: () => void) => {
      flushPendingDataChangeRef.current = flushPendingDataChange
    },
    [],
  )

  const handleRevertLocalChanges = useCallback(() => {
    // Clear only local draft state and restore last saved server data.
    clearDraft()
    clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
    savedSnapshotRef.current = null

    justSavedRef.current = true
    isFirstDataChangeRef.current = true
    hasUnsavedChangesRef.current = false
    setHasUnsavedChanges(false)

    setCurrentFlowData(flowData)
    setCanvasHydrateToken((token) => token + 1)
    latestFlowDataRef.current = flowData
    editorBaseUpdatedAtRef.current = switchboard?.updated_at
    setPendingNodeType(null)

    setIsRevertDialogOpen(false)

    toast?.create({
      title: text(
        t,
        'switchboard.edit.revert.success',
        'Local changes reverted',
      ),
      description: text(
        t,
        'switchboard.edit.revert.successDescription',
        'Restored your last saved switchboard.',
      ),
      type: 'success',
    })
  }, [
    clearDraft,
    extensionDialPlan,
    flowData,
    switchboard?.updated_at,
    t,
    toast,
  ])

  const parseTimestamp = useCallback((value?: string): number | null => {
    if (!value) return null
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
  }, [])

  const detectNewerServerVersion = useCallback(async (): Promise<
    'none' | 'needs_confirmation' | 'acknowledged'
  > => {
    if (!switchboard) return 'none'
    if (updateMutation.isPending || isDeletingSwitchboard) return 'none'
    if (isFocusRefreshInFlightRef.current) return 'none'

    isFocusRefreshInFlightRef.current = true
    try {
      const refetched = await refetchSwitchboard()
      const latestSwitchboard = refetched.data
      if (!latestSwitchboard?.updated_at || !latestSwitchboard?.json_data) {
        return 'none'
      }

      const latestServerMs = parseTimestamp(latestSwitchboard.updated_at)
      const editorBaseMs = parseTimestamp(editorBaseUpdatedAtRef.current)

      // If editor base is unknown, initialize it and avoid surprising prompts.
      if (editorBaseMs === null) {
        editorBaseUpdatedAtRef.current = latestSwitchboard.updated_at
        return 'none'
      }

      if (latestServerMs === null || latestServerMs <= editorBaseMs) {
        return 'none'
      }

      if (
        acknowledgedConflictUpdatedAtRef.current ===
        latestSwitchboard.updated_at
      ) {
        return 'acknowledged'
      }

      const newerServerFlowData = deserializeSwitchboardData(
        latestSwitchboard.json_data,
      )
      setPendingServerFlowData(newerServerFlowData)
      setPendingServerUpdatedAt(latestSwitchboard.updated_at)
      setIsVersionConflictDialogOpen(true)
      return 'needs_confirmation'
    } catch {
      // Best-effort freshness check; ignore transient fetch errors on focus.
      return 'none'
    } finally {
      isFocusRefreshInFlightRef.current = false
    }
  }, [
    isDeletingSwitchboard,
    parseTimestamp,
    refetchSwitchboard,
    switchboard,
    updateMutation.isPending,
  ])

  const handleLoadNewerServerVersion = useCallback(() => {
    if (!pendingServerFlowData || !pendingServerUpdatedAt) {
      setIsVersionConflictDialogOpen(false)
      return
    }

    clearDraft()
    clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
    savedSnapshotRef.current = null
    justSavedRef.current = true
    isFirstDataChangeRef.current = true
    hasUnsavedChangesRef.current = false
    setHasUnsavedChanges(false)
    setCurrentFlowData(pendingServerFlowData)
    setCanvasHydrateToken((token) => token + 1)
    latestFlowDataRef.current = pendingServerFlowData
    editorBaseUpdatedAtRef.current = pendingServerUpdatedAt
    acknowledgedConflictUpdatedAtRef.current = undefined
    setPendingNodeType(null)
    setPendingServerFlowData(null)
    setPendingServerUpdatedAt(undefined)
    setIsVersionConflictDialogOpen(false)

    toast?.create({
      title: text(
        t,
        'switchboard.edit.refresh.newerVersionTitle',
        'Switchboard refreshed',
      ),
      description: text(
        t,
        'switchboard.edit.refresh.newerVersionDescription',
        'A newer saved version was detected and loaded.',
      ),
      type: 'info',
    })
  }, [
    clearDraft,
    extensionDialPlan,
    pendingServerFlowData,
    pendingServerUpdatedAt,
    t,
    toast,
  ])

  const handleKeepWorkingWithLocalVersion = useCallback(() => {
    acknowledgedConflictUpdatedAtRef.current = pendingServerUpdatedAt
    setPendingServerFlowData(null)
    setPendingServerUpdatedAt(undefined)
    setIsVersionConflictDialogOpen(false)
  }, [pendingServerUpdatedAt])

  const refreshIfServerNewer = useCallback(async (): Promise<boolean> => {
    if (!switchboard) return false
    const status = await detectNewerServerVersion()
    return status === 'needs_confirmation'
  }, [detectNewerServerVersion, switchboard])

  useEffect(() => {
    const runFocusRefresh = async (trigger: 'mount' | 'focus') => {
      const now = Date.now()
      if (
        trigger === 'focus' &&
        now - lastFocusRefreshAtRef.current < FOCUS_REFRESH_THROTTLE_MS
      ) {
        return
      }
      if (isReferenceRefreshInFlightRef.current) return

      isReferenceRefreshInFlightRef.current = true
      if (trigger === 'focus') {
        lastFocusRefreshAtRef.current = now
      }

      try {
        const tasks: Array<Promise<unknown>> = [detectNewerServerVersion()]

        if (pbxId && pbxId > 0) {
          tasks.push(refetchPbx())
        }

        if (onRefreshReferenceData) {
          tasks.push(Promise.resolve(onRefreshReferenceData()))
        }

        await Promise.allSettled(tasks)
      } finally {
        isReferenceRefreshInFlightRef.current = false
      }
    }

    if (!hasRunInitialRefreshRef.current && switchboard) {
      hasRunInitialRefreshRef.current = true
      void runFocusRefresh('mount')
    }

    const handleWindowFocus = () => {
      void runFocusRefresh('focus')
    }

    const handleVisibilityChange = () => {
      // Most browsers fire both visibilitychange and focus on tab return.
      // runFocusRefresh throttles duplicate calls within a short window.
      if (document.visibilityState === 'visible') {
        void runFocusRefresh('focus')
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    detectNewerServerVersion,
    onRefreshReferenceData,
    pbxId,
    refetchPbx,
    switchboard,
  ])

  const handleFlowDataChange = useCallback(
    (data: SwitchboardFlowData) => {
      // Canvas echoes onDataChange after we set currentFlowData on save - ignore it
      if (justSavedRef.current) {
        justSavedRef.current = false
        latestFlowDataRef.current = data
        return
      }

      // Skip marking as unsaved on initial render (canvas fires onDataChange on mount)
      const isFirstCall = isFirstDataChangeRef.current
      if (isFirstCall) {
        isFirstDataChangeRef.current = false
        latestFlowDataRef.current = data
        return
      }

      let nextFlowDataForDraft = data

      // Clear validation errors when user makes changes
      const hasDataChanges = data.nodes.some((node) => {
        const existingNode = currentFlowData.nodes.find((n) => n.id === node.id)
        if (!existingNode) return false

        const existingData = { ...existingNode.data }
        const newData = { ...node.data }
        delete existingData.validationErrors
        delete newData.validationErrors

        return JSON.stringify(existingData) !== JSON.stringify(newData)
      })

      if (hasDataChanges) {
        const nodesWithoutErrors = data.nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            validationErrors: undefined,
          },
        }))
        nextFlowDataForDraft = {
          ...data,
          nodes: nodesWithoutErrors,
        }
      }

      const hasChangesSinceLastSnapshot = hasDraftRelevantChanges(
        latestFlowDataRef.current,
        nextFlowDataForDraft,
        signatureRefContext,
      )
      latestFlowDataRef.current = nextFlowDataForDraft
      if (!hasChangesSinceLastSnapshot) {
        return
      }

      saveDraft(nextFlowDataForDraft)
      setHasUnsavedChanges(true)

      if (savedSnapshotRef.current) {
        savedSnapshotRef.current = null
        clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
      }

      const structureSignature = JSON.stringify({
        nodeIds: nextFlowDataForDraft.nodes?.map((n) => n.id).sort() || [],
        edgeIds: nextFlowDataForDraft.edges?.map((e) => e.id).sort() || [],
        nodePositions:
          nextFlowDataForDraft.nodes?.map((n) => ({
            id: n.id,
            x: n.position.x,
            y: n.position.y,
          })) || [],
      })

      if (lastStructureRef.current !== structureSignature) {
        lastStructureRef.current = structureSignature
        setCurrentFlowData(nextFlowDataForDraft)
      }
    },
    [currentFlowData.nodes, extensionDialPlan, saveDraft, signatureRefContext],
  )

  // Handle saving the switchboard
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!switchboard) return false

    if (
      !isSwitchboardSaveTargetCurrent(
        extensionDialPlan,
        switchboard.extension_dial_plan,
      )
    ) {
      toast?.create({
        title: text(
          t,
          'switchboard.edit.save.cannotSave',
          'Cannot save switchboard',
        ),
        description: text(
          t,
          'switchboard.edit.save.targetMismatch',
          'The loaded switchboard does not match this page. Refresh before saving.',
        ),
        type: 'error',
      })
      return false
    }

    // Flush pending input edits (many node editors commit on blur), then flush
    // any debounced canvas onDataChange call before reading latestFlowDataRef.
    flushPendingInputBeforeSave(flushPendingDataChangeRef.current)

    // Guard against overwriting newer DB state if this tab is stale.
    const refreshedFromServer = await refreshIfServerNewer()
    if (refreshedFromServer) {
      return false
    }

    // Use the latest flow data from the ref (includes all data changes, even if state wasn't updated)
    const flowDataToSend: SwitchboardFlowData = {
      nodes: latestFlowDataRef.current.nodes || [],
      edges: latestFlowDataRef.current.edges || [],
    }

    const routeConflicts = findFixedHandleRouteConflicts(flowDataToSend)
    if (routeConflicts.length > 0) {
      toast?.create({
        title: text(
          t,
          'switchboard.edit.save.cannotSave',
          'Cannot save switchboard',
        ),
        description: getFixedHandleRouteConflictDescription({
          conflict: routeConflicts[0],
          flowData: flowDataToSend,
          schedules,
          t,
        }),
        type: 'error',
      })
      return false
    }

    // A Schedule node with no schedule selected persists as `schedule_id: null`,
    // and FreeSWITCH then routes every caller down the Closed branch -- silently,
    // during business hours (VOI-665). Only nodes a call can actually reach are
    // blocking: disconnected leftover Schedule boxes are common on older
    // switchboards and are not this editor user's problem to fix.
    const unconfiguredSchedules =
      findUnconfiguredConnectedScheduleNodes(flowDataToSend)
    if (unconfiguredSchedules.length > 0) {
      toast?.create({
        title: text(
          t,
          'switchboard.edit.save.cannotSave',
          'Cannot save switchboard',
        ),
        description:
          unconfiguredSchedules.length === 1
            ? text(
                t,
                'switchboard.edit.unconfiguredSchedule.description',
                'A Schedule node in this call flow has no schedule selected. Pick a schedule before saving, or disconnect the node.',
              )
            : text(
                t,
                'switchboard.edit.unconfiguredSchedule.descriptionPlural',
                '{{count}} Schedule nodes in this call flow have no schedule selected. Pick a schedule for each before saving, or disconnect them.',
                { count: unconfiguredSchedules.length },
              ),
        type: 'error',
      })
      return false
    }

    // Convert to legacy format for API compatibility
    // Backend requires json_data to be a list (array)
    // Pass messaging groups to convert SMS sendFrom IDs to phone numbers
    const oldFormatData = convertReactFlowToLegacyFormat(
      flowDataToSend,
      messagingGroups,
    )
    const oldFormatArray = [oldFormatData] // Wrap in array - API validation requires list

    // Extract all nodes from legacy format for validation
    const allLegacyNodes: LegacyFormatNode[] = []
    Object.values(oldFormatData.nodes).forEach((nodeOrArray) => {
      if (Array.isArray(nodeOrArray)) {
        allLegacyNodes.push(...nodeOrArray)
      } else if (nodeOrArray) {
        allLegacyNodes.push(nodeOrArray)
      }
    })

    // Build mapping from legacy node ID to React Flow node ID
    // Legacy node ID is either 'start_node' or numeric ID extracted from React Flow node ID
    const legacyToReactFlowIdMap = new Map<string, string>()
    flowDataToSend.nodes.forEach((node) => {
      if (node.type === 'start') {
        // Start node: legacy ID is always 'start_node'
        const startNode = allLegacyNodes.find(
          (ln) => ln.kind === 'start_node' && ln.id === 'start_node',
        )
        if (startNode) {
          legacyToReactFlowIdMap.set('start_node', node.id)
        }
      } else {
        // Non-start nodes: legacy ID is numeric extracted from React Flow ID
        // Extract numeric ID from React Flow node ID (e.g., "sms_notification-123" -> "123")
        const numericId = extractNumericId(node.id)
        // Find legacy node with matching numeric ID
        const legacyNode = allLegacyNodes.find((ln) => {
          if (ln.kind === 'start_node') return false
          // Legacy node ID should match the extracted numeric ID
          return ln.id === numericId
        })
        if (legacyNode) {
          legacyToReactFlowIdMap.set(legacyNode.id, node.id)
        }
      }
    })

    // Validate required field values before saving
    const requiredFieldValidation = validateRequiredFieldValues(
      allLegacyNodes,
      { externalTransferPhoneNumberDigitLimits },
    )
    if (!requiredFieldValidation.valid) {
      const firstError = requiredFieldValidation.errors[0]
      const firstErrorMessage = text(
        t,
        firstError.messageKey ?? 'switchboard.validation.requiredField.generic',
        firstError.message,
      )
      const firstErrorNodeLabel = firstError.nodeLabel || firstError.nodeKind
      toast?.create({
        title: text(
          t,
          'switchboard.edit.save.cannotSave',
          'Cannot save switchboard',
        ),
        description: text(
          t,
          'switchboard.edit.save.requiredFieldError',
          '{{node}}: {{message}}',
          {
            node: firstErrorNodeLabel,
            message: firstErrorMessage,
          },
        ),
        type: 'error',
      })

      // Update nodes with validation errors
      const updatedNodes = flowDataToSend.nodes.map((node) => {
        // Find errors for this node by matching legacy node ID
        const nodeErrors: Record<string, string> = {}
        requiredFieldValidation.errors.forEach((error) => {
          const reactFlowId = legacyToReactFlowIdMap.get(error.nodeId)
          if (reactFlowId === node.id) {
            // Map legacy field names to React Flow field names
            let fieldName = error.field
            if (error.field === 'new_caller_id') {
              fieldName = 'newCallerId'
            } else if (error.field === 'number') {
              fieldName = 'phoneNumber'
            } else if (error.field === 'sms_recipient') {
              fieldName = 'phoneNumber'
            } else if (error.field === 'agent_id') {
              fieldName = 'agentId'
            } else if (error.field === 'message') {
              fieldName = 'message'
            }
            nodeErrors[fieldName] = text(
              t,
              error.messageKey ??
                'switchboard.validation.requiredField.generic',
              error.message,
            )
          }
        })

        if (Object.keys(nodeErrors).length > 0) {
          return {
            ...node,
            data: {
              ...node.data,
              validationErrors: nodeErrors,
            },
          }
        }
        return node
      })

      // Update currentFlowData with errors
      const flowDataWithErrors: SwitchboardFlowData = {
        ...flowDataToSend,
        nodes: updatedNodes,
      }
      setCurrentFlowData(flowDataWithErrors)
      latestFlowDataRef.current = flowDataWithErrors

      // Log all errors for debugging
      console.error('[Switchboard Save] Required field validation failed:', {
        errors: requiredFieldValidation.errors,
        legacyToReactFlowIdMap: Array.from(legacyToReactFlowIdMap.entries()),
        updatedNodes: updatedNodes.filter((n) => n.data.validationErrors),
      })

      return false
    }

    // Validate spec compliance before saving
    const validation = validateSpecCompliance(oldFormatData)
    if (!validation.valid) {
      const errorMessages = validation.errors.map((err) => {
        const translatedMessage = text(
          t,
          err.messageKey ?? 'switchboard.validation.spec.generic',
          err.message,
        )
        const location = err.nodeId
          ? text(
              t,
              'switchboard.validation.spec.nodeLocation',
              'Node {{nodeId}} ({{nodeKind}}): ',
              {
                nodeId: err.nodeId,
                nodeKind:
                  err.nodeKind ||
                  text(
                    t,
                    'switchboard.validation.spec.unknownNodeKind',
                    'unknown',
                  ),
              },
            )
          : err.nodeKind
            ? text(
                t,
                'switchboard.validation.spec.nodeTypeLocation',
                'Node type {{nodeKind}}: ',
                { nodeKind: err.nodeKind },
              )
            : ''
        const field = err.field
          ? text(
              t,
              'switchboard.validation.spec.fieldLocation',
              'Field {{field}}: ',
              {
                field: err.field,
              },
            )
          : ''
        return `${location}${field}${translatedMessage}`
      })

      const errorMessage =
        errorMessages.length > 0
          ? text(
              t,
              'switchboard.validation.spec.summaryWithErrors',
              'Validation failed:\n{{errorList}}{{moreErrors}}',
              {
                errorList: errorMessages.slice(0, 5).join('\n'),
                moreErrors:
                  errorMessages.length > 5
                    ? text(
                        t,
                        'switchboard.validation.spec.moreErrors',
                        '\n... and {{count}} more errors',
                        { count: errorMessages.length - 5 },
                      )
                    : '',
              },
            )
          : text(
              t,
              'switchboard.validation.spec.summaryNoDetails',
              'Switchboard data does not comply with routing specification',
            )

      toast?.create({
        title: text(
          t,
          'switchboard.edit.save.cannotSave',
          'Cannot save switchboard',
        ),
        description: errorMessage,
        type: 'error',
      })
      return false
    }

    // Backend only accepts json_data in body, extension_dial_plan goes in URL path
    // JSON Serialization Format:
    // - Legacy format requires compact JSON (no spaces): {"nodes":{"start_node":{...}}}
    // - Backend is responsible for serializing json_data to compact format to match legacy
    // - Frontend sends object/array, backend serializes it
    // - Legacy format: JSON.stringify(data, null, 0).replace(/\s+/g, '')
    // - If backend doesn't handle compact format, we would need to serialize here and send as string
    // - Current implementation: Backend handles serialization (verify this is working correctly)
    const requestPayload = {
      extension_dial_plan: extensionDialPlan,
      json_data: oldFormatArray, // Must be a list/array (backend will serialize to compact JSON)
    }

    try {
      const preSaveBaseUpdatedAt =
        editorBaseUpdatedAtRef.current ?? switchboard?.updated_at
      const savedSwitchboard = await updateMutation.mutateAsync(requestPayload)

      // Clear validationErrors from node data on successful save
      const nodesWithoutErrors = latestFlowDataRef.current.nodes.map(
        (node) => ({
          ...node,
          data: {
            ...node.data,
            validationErrors: undefined,
          },
        }),
      )
      const flowDataWithoutErrors: SwitchboardFlowData = {
        ...latestFlowDataRef.current,
        nodes: nodesWithoutErrors,
      }
      const savedSignature = getPersistedGraphSignature(
        flowDataWithoutErrors,
        signatureRefContext,
      )
      if (savedSignature.status === 'ready') {
        const savedSnapshot: SavedSwitchboardSnapshot = {
          switchboardId: extensionDialPlan,
          flowData: flowDataWithoutErrors,
          contentSignature: savedSignature.value,
          savedAt: Date.now(),
          updatedAt: savedSwitchboard?.updated_at,
        }
        savedSnapshotRef.current = savedSnapshot
        saveSwitchboardSnapshotToStorage(savedSnapshot)
      } else {
        savedSnapshotRef.current = null
        clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
      }

      // After successful mutation, keep the just-saved local graph on screen.
      // We may hydrate from one refetch later if it is clearly newer.
      const localPostSaveResult = resolvePostSaveFlowData({
        localFlowData: flowDataWithoutErrors,
        preSaveBaseUpdatedAt,
        mutationUpdatedAt: savedSwitchboard?.updated_at,
        refetchedSwitchboard: null,
        parseTimestamp,
        deserializeSwitchboardData,
        refContext: signatureRefContext,
      })

      // Clear the draft from localStorage immediately after successful save.
      clearDraft()

      // Suppress canvas onDataChange echo - it would reset hasUnsavedChanges to true
      justSavedRef.current = true
      hasUnsavedChangesRef.current = false
      acknowledgedConflictUpdatedAtRef.current = undefined
      setHasUnsavedChanges(false)

      setCurrentFlowData(localPostSaveResult.flowData)
      setCanvasHydrateToken((token) => token + 1)
      latestFlowDataRef.current = localPostSaveResult.flowData
      editorBaseUpdatedAtRef.current = localPostSaveResult.editorBaseUpdatedAt

      let refetchedSwitchboard: {
        updated_at?: string
        json_data?: unknown
      } | null = null
      try {
        // Use a direct API read (not React Query's refetch) so a briefly-stale
        // GET response cannot overwrite the authoritative json_data we just
        // saved. This read is only used to detect a genuinely newer concurrent
        // edit below.
        const refetched = await api.getSwitchboard(extensionDialPlan)
        if (refetched.ok && refetched.data) {
          refetchedSwitchboard = {
            updated_at: refetched.data.updated_at,
            json_data: refetched.data.json_data,
          }
        }
      } catch {
        // Keep local post-save state when the freshness read fails.
      }

      const postRefetchResult = resolvePostSaveFlowData({
        localFlowData: flowDataWithoutErrors,
        preSaveBaseUpdatedAt,
        mutationUpdatedAt: savedSwitchboard?.updated_at,
        refetchedSwitchboard,
        parseTimestamp,
        deserializeSwitchboardData,
        refContext: signatureRefContext,
      })
      if (postRefetchResult.hydratedFromRefetch) {
        justSavedRef.current = true
        setCurrentFlowData(postRefetchResult.flowData)
        setCanvasHydrateToken((token) => token + 1)
        latestFlowDataRef.current = postRefetchResult.flowData
        editorBaseUpdatedAtRef.current = postRefetchResult.editorBaseUpdatedAt
      } else if (postRefetchResult.reason === 'matched_refetch') {
        editorBaseUpdatedAtRef.current = postRefetchResult.editorBaseUpdatedAt
        savedSnapshotRef.current = null
        clearSavedSwitchboardSnapshotFromStorage(extensionDialPlan)
      }

      queryClient.invalidateQueries({ queryKey: ['switchboards'] })
      queryClient.invalidateQueries({
        queryKey: ['switchboard', extensionDialPlan],
      })

      toast?.create({
        title: text(t, 'switchboard.edit.save.success', 'Switchboard saved'),
        type: 'success',
      })
      return true
    } catch (err) {
      // Keep unsaved state so user retains "unsaved changes" protection and can retry
      hasUnsavedChangesRef.current = true
      setHasUnsavedChanges(true)
      const errorMessage =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err
            ? String(err.message)
            : String(err) ||
              text(
                t,
                'switchboard.edit.save.unexpectedError',
                'An unexpected error occurred.',
              )
      toast?.create({
        title: text(
          t,
          'switchboard.edit.save.failed',
          'Failed to save switchboard',
        ),
        description: errorMessage,
        type: 'error',
      })
      return false
    }
  }, [
    switchboard,
    updateMutation,
    toast,
    messagingGroups,
    clearDraft,
    parseTimestamp,
    refreshIfServerNewer,
    api,
    extensionDialPlan,
    externalTransferPhoneNumberDigitLimits,
    queryClient,
    signatureRefContext,
    schedules,
    t,
  ])

  // Early returns AFTER all hooks
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        w="100%"
      >
        <LoadingState
          title={text(t, 'switchboard.edit.loading', 'Loading switchboard...')}
        />
      </Box>
    )
  }

  if (error || !switchboard) {
    // Check if it's a 404 error
    const apiError = error as { status?: number; message?: string } | undefined
    const is404 = apiError?.status === 404

    return (
      <Box p={8} textAlign="center">
        <Text color="fg.error" fontSize="lg" fontWeight="bold" mb={2}>
          {is404
            ? text(
                t,
                'switchboard.edit.notFound.title',
                'Switchboard Not Found',
              )
            : text(
                t,
                'switchboard.edit.notFound.errorLoading',
                'Error loading switchboard: {{message}}',
                {
                  message:
                    apiError?.message ||
                    text(
                      t,
                      'switchboard.edit.notFound.unknownError',
                      'Unknown error',
                    ),
                },
              )}
        </Text>
        {is404 && (
          <Box mt={4}>
            <Text color="fg.muted" mb={4}>
              {text(
                t,
                'switchboard.edit.notFound.description',
                'The switchboard with extension {{extensionDialPlan}} does not exist in the API.',
                { extensionDialPlan },
              )}
            </Text>
            <Text color="fg.muted" fontSize="sm" mb={2}>
              {text(
                t,
                'switchboard.edit.notFound.thisCouldMean',
                'This could mean:',
              )}
            </Text>
            <Box as="ul" textAlign="left" maxW="md" mx="auto" mt={2} mb={4}>
              <Text as="li" color="fg.muted" fontSize="sm">
                {text(
                  t,
                  'switchboard.edit.notFound.reasonDeleted',
                  'The switchboard was deleted',
                )}
              </Text>
              <Text as="li" color="fg.muted" fontSize="sm">
                {text(
                  t,
                  'switchboard.edit.notFound.reasonNoData',
                  'The extension exists but has no switchboard data',
                )}
              </Text>
              <Text as="li" color="fg.muted" fontSize="sm">
                {text(
                  t,
                  'switchboard.edit.notFound.reasonCorrupted',
                  'The data is corrupted beyond recovery',
                )}
              </Text>
            </Box>
            <Button
              variant="outline"
              colorPalette="neutral"
              onClick={() => navigate(listRoute)}
              borderRadius="lg"
            >
              {text(
                t,
                'switchboard.edit.notFound.backToList',
                '← Back to Switchboards List',
              )}
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      position="relative"
      w="100%"
      h="100%"
      bg="bg.default"
      overflow="hidden"
    >
      {/* Header bar - fixed at top */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={10}
        bg="bg.panel"
        borderBottomWidth={1}
        borderColor="border.default"
        p={4}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={!hasOldFormat ? 4 : 0}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              aria-label={text(
                t,
                'switchboard.edit.header.backToList',
                'Back to switchboards',
              )}
              variant="ghost"
              size="sm"
              onClick={() => navigate(listRoute)}
            >
              <ArrowLeft />
            </IconButton>
            <Heading
              fontSize="20px"
              fontWeight="800"
              lineHeight="28px"
              color="fg.default"
              marginBottom="0"
            >
              {switchboardDisplayName ||
                text(
                  t,
                  'switchboard.edit.header.defaultName',
                  'Switchboard {{extensionDialPlan}}',
                  {
                    extensionDialPlan: switchboard.extension_dial_plan,
                  },
                )}
            </Heading>
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            {!hasOldFormat && (
              <Button
                size="sm"
                variant="outline"
                colorPalette="neutral"
                onClick={() => setIsRevertDialogOpen(true)}
                disabled={!hasUnsavedChanges || updateMutation.isPending}
                px={4}
                py={2}
                borderRadius="lg"
              >
                {text(
                  t,
                  'switchboard.edit.header.refreshSwitchboard',
                  'Refresh Switchboard',
                )}
              </Button>
            )}
            {onDelete && (
              <IconButton
                aria-label={text(
                  t,
                  'switchboard.edit.header.deleteSwitchboard',
                  'Delete Switchboard',
                )}
                size="sm"
                variant="solid"
                colorPalette="error"
                onClick={handleDelete}
                loading={isDeletingSwitchboard}
                disabled={updateMutation.isPending || isDeletingSwitchboard}
                px={4}
                py={2}
                borderRadius="lg"
                css={{ '& svg': { color: 'inherit' } }}
              >
                <Trash2 />
              </IconButton>
            )}
            {!hasOldFormat && (
              <Button
                size="sm"
                variant="solid"
                colorPalette="brand"
                onClick={handleSave}
                loading={updateMutation.isPending}
                disabled={hasOldFormat || hasFixedHandleRouteConflicts}
                px={4}
                py={2}
                borderRadius="lg"
              >
                {text(t, 'switchboard.edit.header.save', 'Save')}
              </Button>
            )}
          </Box>
        </Box>
        {!hasOldFormat && (
          <Box>
            <NodePalette
              onAddNode={handleAddNode}
              disabled={hasOldFormat}
              isStaff={isStaff}
              t={t}
            />
          </Box>
        )}
      </Box>

      {/* Warning banner - below header */}
      {hasOldFormat && (
        <Box
          position="absolute"
          top="73px"
          left={0}
          right={0}
          zIndex={9}
          p={4}
          bg="bg.warning"
          borderBottomWidth={1}
          borderColor="border.warning"
        >
          <Text fontSize="sm" fontWeight="medium" color="fg.warning">
            {text(
              t,
              'switchboard.edit.oldFormat.warning',
              '⚠️ Warning: Switchboard data is in old format',
            )}
          </Text>
          <Text fontSize="xs" color="fg.muted" mt={1}>
            {text(
              t,
              'switchboard.edit.oldFormat.description',
              "This switchboard was created with the old editor. The visual flow cannot be displayed until it's migrated to the new format.",
            )}
          </Text>
        </Box>
      )}

      {!hasOldFormat && (
        <FixedHandleRouteConflictToast
          descriptions={routeConflictDescriptions}
          extensionDialPlan={extensionDialPlan}
          toast={toast}
          t={t}
        />
      )}

      {/* Canvas - full screen below header */}
      <Box
        position="absolute"
        top={hasOldFormat ? '145px' : '120px'}
        left={0}
        right={0}
        bottom={0}
        bg="bg.emphasized"
      >
        {currentFlowData.nodes.length === 0 &&
        currentFlowData.edges.length === 0 &&
        !hasOldFormat ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            h="100%"
          >
            <Text color="fg.muted" mb={4} fontSize="lg">
              {text(
                t,
                'switchboard.edit.canvas.emptyTitle',
                'No flow configured yet',
              )}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {text(
                t,
                'switchboard.edit.canvas.emptyDescription',
                'Click "Add Node" to start building your call flow',
              )}
            </Text>
          </Box>
        ) : (
          <SwitchboardCanvas
            api={api}
            initialData={currentFlowData}
            hydrateToken={canvasHydrateToken}
            readOnly={hasOldFormat}
            onDataChange={handleFlowDataChange}
            registerFlushPendingDataChange={
              handleRegisterFlushPendingDataChange
            }
            pendingNodeType={pendingNodeType}
            onNodeAdded={handleNodeAdded}
            toast={toast}
            dayNightSettings={dayNightSettings}
            pbxId={pbxId}
            colorMode={colorMode}
            t={t}
          />
        )}
      </Box>

      {blocker?.state === 'blocked' && (
        <Modal
          open
          zIndex={Z_INDEX.MODAL}
          onOpenChange={({ open }) => {
            if (!open && blocker.state === 'blocked') {
              blocker.reset?.()
            }
          }}
          title={text(
            t,
            'switchboard.edit.unsavedChanges.title',
            'Unsaved Changes',
          )}
          footer={
            <Box display="flex" gap={3} justifyContent="flex-end" w="100%">
              <Button
                variant="outline"
                onClick={() => {
                  if (blocker.state === 'blocked') blocker.reset?.()
                }}
                px={5}
                py={2}
                borderRadius="lg"
              >
                {text(t, 'switchboard.edit.unsavedChanges.stay', 'Stay')}
              </Button>
              <Button
                colorPalette="brand"
                loading={updateMutation.isPending}
                disabled={hasFixedHandleRouteConflicts}
                onClick={async () => {
                  const saved = await handleSave()
                  if (saved && blocker.state === 'blocked') {
                    blocker.proceed?.()
                  }
                }}
                px={5}
                py={2}
                borderRadius="lg"
              >
                {text(t, 'switchboard.edit.unsavedChanges.save', 'Save')}
              </Button>
              <Button
                colorPalette="error"
                onClick={() => {
                  if (blocker.state === 'blocked') blocker.proceed?.()
                }}
                px={5}
                py={2}
                borderRadius="lg"
              >
                {text(t, 'switchboard.edit.unsavedChanges.leave', 'Leave')}
              </Button>
            </Box>
          }
        >
          <Text color="fg.muted" lineHeight="tall">
            {text(
              t,
              'switchboard.edit.unsavedChanges.body',
              'You have unsaved changes. Are you sure you want to leave?',
            )}
          </Text>
        </Modal>
      )}

      {onDelete && (
        <SwitchboardDeleteDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          displayName={
            switchboardDisplayName ||
            text(
              t,
              'switchboard.edit.header.defaultName',
              'Switchboard {{extensionDialPlan}}',
              {
                extensionDialPlan: switchboard.extension_dial_plan,
              },
            )
          }
          onConfirm={() => {
            void handleConfirmDelete()
          }}
          isDeleting={isDeletingSwitchboard}
          t={t}
        />
      )}

      <ConfirmDialog
        open={isRevertDialogOpen}
        onClose={() => setIsRevertDialogOpen(false)}
        title={text(
          t,
          'switchboard.edit.revert.title',
          'Revert local changes?',
        )}
        description={text(
          t,
          'switchboard.edit.revert.body',
          'This removes changes made in this browser session and reloads your last saved switchboard. It will not delete what is already saved.',
        )}
        confirmLabel={text(
          t,
          'switchboard.edit.revert.confirm',
          'Revert Changes',
        )}
        cancelLabel={text(t, 'switchboard.edit.revert.cancel', 'Cancel')}
        onConfirm={handleRevertLocalChanges}
        maxWidth="460px"
        variant="destructive"
        zIndex={Z_INDEX.MODAL}
      />

      <Modal
        open={isVersionConflictDialogOpen}
        onOpenChange={(e) => setIsVersionConflictDialogOpen(e.open)}
        title={text(
          t,
          'switchboard.edit.versionConflict.title',
          'New updates are available',
        )}
        maxWidth="520px"
        zIndex={Z_INDEX.MODAL}
        footer={
          <Box display="flex" gap={3} justifyContent="flex-end" w="100%">
            <Button
              variant="outline"
              onClick={handleKeepWorkingWithLocalVersion}
              px={5}
              py={2}
              borderRadius="lg"
            >
              {text(
                t,
                'switchboard.edit.versionConflict.keepWorking',
                'Keep My Changes',
              )}
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleLoadNewerServerVersion}
              px={5}
              py={2}
              borderRadius="lg"
            >
              {text(
                t,
                'switchboard.edit.versionConflict.loadLatest',
                'Load Latest Changes',
              )}
            </Button>
          </Box>
        }
      >
        <Text color="fg.muted" lineHeight="tall">
          {text(
            t,
            'switchboard.edit.versionConflict.body',
            'Someone else saved changes to this switchboard. Keep your current edits, or load the latest saved version.',
          )}
        </Text>
      </Modal>
    </Box>
  )
}
